export const WEEKDAY_LABELS = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];

const dayHeadingFormat = new Intl.DateTimeFormat('ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
});

const dayLabelFormat = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
});

const matchPlural = new Intl.PluralRules('ru');

const MATCH_FORMS = {
    one: 'матч',
    few: 'матча',
    many: 'матчей',
    other: 'матчей',
};

/**
 * @param {Date} date
 * @returns {string} local Y-m-d
 */
export function formatDateKey(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * @param {string} dateKey local Y-m-d
 * @returns {Date}
 */
export function parseDateKey(dateKey) {
    const [year, month, day] = dateKey.split('-').map(Number);
    return new Date(year, month - 1, day);
}

/**
 * Monday-first month grid, padded to a whole number of weeks.
 *
 * @param {number} year
 * @param {number} month 1-12
 * @param {Record<string, Array>} gamesByDate
 * @param {Date} [today]
 */
export function buildCalendarCells(year, month, gamesByDate = {}, today = new Date()) {
    const first = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    const mondayOffset = (first.getDay() + 6) % 7;
    const todayKey = formatDateKey(today);
    const cells = [];

    for (let i = 0; i < mondayOffset; i += 1) {
        cells.push(emptyCell());
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
        const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        cells.push({
            day,
            dateKey,
            games: gamesByDate[dateKey] || [],
            isToday: dateKey === todayKey,
        });
    }

    while (cells.length % 7 !== 0) {
        cells.push(emptyCell());
    }

    return cells;
}

/**
 * Today when it has games; otherwise the nearest match day
 * (future wins a tie); otherwise today if it falls in this month.
 *
 * @param {Array<{ dateKey: string|null, games: Array }>} cells
 * @param {Date} [today]
 * @returns {string|null}
 */
export function pickDefaultDateKey(cells, today = new Date()) {
    const todayKey = formatDateKey(today);
    const inMonth = cells.filter((cell) => cell.dateKey);
    const todayCell = inMonth.find((cell) => cell.dateKey === todayKey);

    if (todayCell?.games.length) {
        return todayKey;
    }

    const withGames = inMonth.filter((cell) => cell.games.length > 0);
    if (!withGames.length) {
        return todayCell?.dateKey ?? null;
    }

    const todayIndex = utcDayIndex(startOfLocalDay(today));
    let bestKey = null;
    let bestDistance = Infinity;
    let bestIndex = -Infinity;

    for (const cell of withGames) {
        const index = utcDayIndex(parseDateKey(cell.dateKey));
        const distance = Math.abs(index - todayIndex);
        if (distance < bestDistance || (distance === bestDistance && index > bestIndex)) {
            bestKey = cell.dateKey;
            bestDistance = distance;
            bestIndex = index;
        }
    }

    return bestKey;
}

/**
 * @param {string} dateKey
 * @returns {string}
 */
export function formatDayHeading(dateKey) {
    return dayHeadingFormat.format(parseDateKey(dateKey));
}

/**
 * @param {string} dateKey
 * @param {number} gameCount
 * @returns {string}
 */
export function formatDayAriaLabel(dateKey, gameCount) {
    const dateLabel = dayLabelFormat.format(parseDateKey(dateKey));
    if (!gameCount) {
        return `${dateLabel}, матчей нет`;
    }

    return `${dateLabel}, ${formatMatchCount(gameCount)}`;
}

/**
 * @param {number} count
 * @returns {string}
 */
export function formatMatchCount(count) {
    const form = MATCH_FORMS[matchPlural.select(count)] || MATCH_FORMS.other;
    return `${count} ${form}`;
}

/**
 * Monday = 0 … Sunday = 6. dateKey is a local Y-m-d.
 *
 * @param {string} dateKey
 * @returns {number}
 */
export function weekdayIndex(dateKey) {
    return (parseDateKey(dateKey).getDay() + 6) % 7;
}

function emptyCell() {
    return { day: null, dateKey: null, games: [], isToday: false };
}

function startOfLocalDay(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function utcDayIndex(date) {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}
